// Phase 5Z.29 — Time Entry Change Requests (Admin approval queue)
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge } from "@/components/Shared";
import DataTable from "@/components/DataTable";
import { CheckCircle2, XCircle, Clock, ListChecks, AlertCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import HelpHint from "@/components/HelpHint";
import FirmFilter from "@/components/FirmFilter";
import { fmtDate, fmtDateTime } from "@/lib/format";

const FIELD_LABELS = {
  work_start: "Arbeit Start",
  work_end: "Arbeit Ende",
  driving_start: "Fahrt Start",
  driving_end: "Fahrt Ende",
  start_km: "KM Start",
  end_km: "KM Ende",
  workplace: "Einsatzort",
  notes: "Notizen",
};

const APPROVE_ROLES = ["admin", "geschaeftsfuehrung", "disponent", "logistik_manager"];

export default function ZeitAnfragen() {
  const { user } = useAuth();
  const canApprove = APPROVE_ROLES.includes(user?.role);
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [rejectModal, setRejectModal] = useState(null); // request to reject
  const [rejectReason, setRejectReason] = useState("");
  const [busyBulk, setBusyBulk] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        api.get("/zeit-change-requests", {
          params: statusFilter && statusFilter !== "all" ? { status: statusFilter } : {},
        }),
        api.get("/zeit-change-requests/_summary"),
      ]);
      setItems(list.data || []);
      setSummary(sum.data || { total: 0, pending: 0, approved: 0, rejected: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Laden");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  async function approve(req) {
    try {
      await api.put(`/zeit-change-requests/${req.id}/approve`);
      toast.success(`Anfrage von ${req.employee_name} freigegeben — Eintrag aktualisiert`);
      setDetail(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  function openReject(req) {
    setRejectReason("");
    setRejectModal(req);
  }

  async function confirmReject() {
    const reason = rejectReason.trim();
    if (reason.length < 3) {
      toast.error("Bitte gib eine Begründung ein (min. 3 Zeichen).");
      return;
    }
    try {
      await api.put(`/zeit-change-requests/${rejectModal.id}/reject`, { rejection_reason: reason });
      toast.success("Anfrage abgelehnt");
      setRejectModal(null);
      setDetail(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  // Phase G-UX (27.05.2026) — Admin-only hard-delete (single + bulk).
  async function deleteOne(req) {
    const ok = window.confirm(
      `Diese Anfrage von ${req.employee_name} (${req.date || "—"}) wird ` +
      `KOMPLETT gelöscht (inklusive Fahrer-Benachrichtigungen). ` +
      `Diese Aktion kann nicht rückgängig gemacht werden.\n\nFortfahren?`
    );
    if (!ok) return;
    try {
      await api.delete(`/zeit-change-requests/${req.id}`);
      toast.success("Anfrage gelöscht");
      setDetail(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Löschen"); }
  }

  async function deleteByStatusBulk(targetStatus) {
    const label = targetStatus === "all" ? "ALLE Anfragen"
      : targetStatus === "pending" ? "alle AUSSTEHENDEN Anfragen"
      : targetStatus === "approved" ? "alle GENEHMIGTEN Anfragen"
      : "alle ABGELEHNTEN Anfragen";
    const count = targetStatus === "all" ? summary.total
      : targetStatus === "pending" ? summary.pending
      : targetStatus === "approved" ? summary.approved
      : summary.rejected;
    if (!count) { toast.info("Keine passenden Anfragen zum Löschen"); return; }
    const ok = window.confirm(
      `${label} (${count}) werden KOMPLETT gelöscht — inkl. zugehöriger ` +
      `Fahrer-Benachrichtigungen. Diese Aktion kann nicht rückgängig gemacht werden.\n\nFortfahren?`
    );
    if (!ok) return;
    setBusyBulk(true);
    try {
      const { data } = await api.post("/zeit-change-requests/bulk-delete", { status: targetStatus });
      toast.success(`${data.deleted_count} Anfragen gelöscht`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Massenlöschen");
    } finally { setBusyBulk(false); }
  }

  return (
    <div className="p-6 md:p-8" data-testid="zeit-anfragen-page">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-zinc-100">Zeit-Anfragen</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Korrektur-Anträge der Fahrer zu eigenen Zeiterfassungs-Einträgen
          </p>
        </div>
        {isAdmin && (
          <div className="inline-flex items-center gap-2" data-testid="zr-admin-cleanup-bar">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Admin · Aufräumen:</span>
            <button onClick={() => deleteByStatusBulk("approved")} disabled={busyBulk || !summary.approved}
                    data-testid="zr-bulk-delete-approved"
                    className="text-xs px-2.5 py-1.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Genehmigte ({summary.approved})
            </button>
            <button onClick={() => deleteByStatusBulk("rejected")} disabled={busyBulk || !summary.rejected}
                    data-testid="zr-bulk-delete-rejected"
                    className="text-xs px-2.5 py-1.5 rounded border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Abgelehnte ({summary.rejected})
            </button>
            <button onClick={() => deleteByStatusBulk("all")} disabled={busyBulk || !summary.total}
                    data-testid="zr-bulk-delete-all"
                    className="text-xs px-2.5 py-1.5 rounded border border-rose-500/40 bg-rose-600/80 text-white hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 font-semibold">
              <Trash2 className="w-3 h-3" /> Alle ({summary.total})
            </button>
          </div>
        )}
      </div>

      {/* Summary cards as filters */}
      <HelpHint title="Wie funktionieren Zeit-Anfragen?" className="mb-4" testid="help-zeit-anfragen">
        <p>Fahrer können über das Fahrer-Portal Korrekturen an ihren bereits eingereichten Zeiterfassungen beantragen (z.B. „Lenkbeginn war 06:30, nicht 07:00").</p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Ausstehend</b>: warten auf deine Entscheidung. Klicke „Ansehen" um die Vorher→Vorschlag-Tabelle zu sehen.</li>
          <li><b>Freigeben</b> ändert den Original-Eintrag direkt + speichert einen Audit-Log + benachrichtigt den Fahrer.</li>
          <li><b>Ablehnen</b> erfordert eine Begründung (mind. 3 Zeichen) — wird dem Fahrer im Portal sichtbar gemacht.</li>
          <li>Nur Rollen <i>Admin / Geschäftsführung / Disponent / Logistik-Manager</i> können entscheiden.</li>
        </ul>
      </HelpHint>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryCard label="Gesamt" value={summary.total} icon={ListChecks} active={statusFilter === "all"}
                     onClick={() => setStatusFilter("all")} testid="zr-card-total" />
        <SummaryCard label="Ausstehend" value={summary.pending} icon={Clock} active={statusFilter === "pending"}
                     color="text-amber-400 border-amber-500/30 bg-amber-500/5"
                     onClick={() => setStatusFilter("pending")} testid="zr-card-pending" />
        <SummaryCard label="Genehmigt" value={summary.approved} icon={CheckCircle2} active={statusFilter === "approved"}
                     color="text-emerald-400 border-emerald-500/30 bg-emerald-500/5"
                     onClick={() => setStatusFilter("approved")} testid="zr-card-approved" />
        <SummaryCard label="Abgelehnt" value={summary.rejected} icon={XCircle} active={statusFilter === "rejected"}
                     color="text-red-400 border-red-500/30 bg-red-500/5"
                     onClick={() => setStatusFilter("rejected")} testid="zr-card-rejected" />
      </div>

      <DataTable
        rows={items}
        loading={loading}
        filters={<FirmFilter compact />}
        searchableFields={[
          "employee_name", "reason", "date",
          (r) => Object.keys(r.proposed_changes || {}).join(" "),
        ]}
        searchPlaceholder="Fahrer, Datum, Begründung, Felder..."
        testid="zeit-anfragen-table"
        emptyTitle="Keine Anfragen"
        emptyHint={statusFilter === "pending" ? "Aktuell sind keine Anfragen offen." : "Versuchen Sie einen anderen Filter."}
        columns={[
          { key: "created_at", label: "Eingereicht", sortable: true,
            render: (r) => <span className="font-mono text-xs text-zinc-300">{fmtDateTime(r.created_at)}</span> },
          { key: "date", label: "Tag", sortable: true,
            render: (r) => <span className="font-mono text-xs text-zinc-200">{r.date || "—"}</span> },
          { key: "employee_name", label: "Fahrer",
            render: (r) => <span className="text-zinc-100 font-medium">{r.employee_name || "—"}</span> },
          { key: "_fields", label: "Felder",
            render: (r) => (
              <div className="flex flex-wrap gap-1 max-w-[28rem]">
                {Object.keys(r.proposed_changes || {}).map((k) => (
                  <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
                    {FIELD_LABELS[k] || k}
                  </span>
                ))}
              </div>
            ) },
          { key: "reason", label: "Begründung",
            render: (r) => <span className="text-xs text-zinc-400 italic line-clamp-1 max-w-[20rem]">{r.reason || "—"}</span> },
          { key: "status", label: "Status", sortable: true,
            render: (r) => <StatusBadge status={r.status === "pending" ? "offen" : r.status === "approved" ? "freigegeben" : "abgelehnt"} /> },
          { key: "_actions", label: "", headerClassName: "text-right", className: "text-right",
            render: (r) => (
              <div className="inline-flex items-center gap-1">
                <button onClick={(e) => { e.stopPropagation(); setDetail(r); }} data-testid={`zr-detail-${r.id}`}
                        className="text-xs px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200">
                  Ansehen
                </button>
                {canApprove && r.status === "pending" && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); approve(r); }} title="Freigeben"
                            data-testid={`zr-approve-${r.id}`}
                            className="p-1.5 text-zinc-400 hover:text-emerald-400">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); openReject(r); }} title="Ablehnen"
                            data-testid={`zr-reject-${r.id}`}
                            className="p-1.5 text-zinc-400 hover:text-red-400">
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
                {isAdmin && (
                  <button onClick={(e) => { e.stopPropagation(); deleteOne(r); }}
                          title="Anfrage komplett löschen (nur Admin)"
                          data-testid={`zr-delete-${r.id}`}
                          className="p-1.5 text-zinc-500 hover:text-rose-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ) },
        ]}
      />

      {detail && (
        <DetailModal
          request={detail}
          canApprove={canApprove && detail.status === "pending"}
          onApprove={() => approve(detail)}
          onReject={() => openReject(detail)}
          onClose={() => setDetail(null)}
        />
      )}

      {rejectModal && (
        <RejectModal
          request={rejectModal}
          reason={rejectReason}
          onReasonChange={setRejectReason}
          onCancel={() => setRejectModal(null)}
          onConfirm={confirmReject}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, active, color, onClick, testid }) {
  const base = "bg-zinc-900 border border-zinc-800 text-zinc-100";
  return (
    <button onClick={onClick} data-testid={testid}
            className={`text-left p-4 rounded-md transition-colors ${active ? (color || "border-blue-500/40 bg-blue-500/5") : base} hover:border-zinc-700`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-400 uppercase tracking-wider font-semibold">{label}</span>
        <Icon className={`w-4 h-4 ${active ? "" : "text-zinc-500"}`} />
      </div>
      <div className="mt-1 text-2xl font-display font-semibold">{value}</div>
    </button>
  );
}

function DetailModal({ request, canApprove, onApprove, onReject, onClose }) {
  const proposed = request.proposed_changes || {};
  const before = request.before || {};
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="zr-detail-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div>
            <h3 className="font-display text-lg font-semibold text-zinc-100">Zeit-Anfrage · {request.employee_name}</h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              Tag <span className="font-mono text-zinc-300">{fmtDate(request.date)}</span> · Eingereicht {fmtDateTime(request.created_at)}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-zinc-100">✕</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Vorgeschlagene Änderungen</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800">
                  <th className="py-1.5 font-medium">Feld</th>
                  <th className="py-1.5 font-medium">Vorher</th>
                  <th className="py-1.5 font-medium">Vorschlag</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(proposed).map(([k, v]) => (
                  <tr key={k} className="border-b border-zinc-800/50 last:border-b-0">
                    <td className="py-2 text-zinc-300 text-xs">{FIELD_LABELS[k] || k}</td>
                    <td className="py-2 text-zinc-500 text-xs line-through">{String(before[k] ?? "—")}</td>
                    <td className="py-2 text-zinc-100 text-xs font-medium">{String(v ?? "—")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {request.reason && (
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Begründung Fahrer</div>
              <div className="text-sm text-zinc-200 bg-zinc-950 border-l-2 border-amber-500 px-3 py-2 rounded">
                {request.reason}
              </div>
            </div>
          )}

          {request.status !== "pending" && (
            <div className="text-xs text-zinc-400">
              <span className="font-medium">{request.status === "approved" ? "Freigegeben" : "Abgelehnt"}</span>
              {" "}am{" "}
              {fmtDateTime(request.approved_at || request.rejected_at)}
              {" "}von{" "}
              {request.approved_by_email || request.rejected_by_email || "—"}
              {request.rejection_reason && (
                <div className="mt-1 text-red-400 italic">Grund: {request.rejection_reason}</div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2 sticky bottom-0 bg-zinc-900">
          {canApprove && (
            <>
              <button onClick={onReject} data-testid="zr-modal-reject"
                      className="text-sm px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1.5">
                <XCircle className="w-4 h-4" /> Ablehnen
              </button>
              <button onClick={onApprove} data-testid="zr-modal-approve"
                      className="text-sm px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> Freigeben
              </button>
            </>
          )}
          <button onClick={onClose}
                  className="text-sm px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100">
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectModal({ request, reason, onReasonChange, onCancel, onConfirm }) {
  const reasonValid = (reason || "").trim().length >= 3;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[60]" data-testid="zr-reject-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-md">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <h3 className="font-display text-base font-semibold text-zinc-100">Anfrage ablehnen</h3>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-sm text-zinc-300">
            Anfrage von <span className="font-semibold text-zinc-100">{request.employee_name}</span> für{" "}
            <span className="font-mono text-zinc-100">{fmtDate(request.date)}</span> ablehnen?
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Begründung (sichtbar für Fahrer) *</label>
            <textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              rows={3}
              autoFocus
              data-testid="zr-reject-reason"
              placeholder="z.B. Werkschutz hat eine andere Uhrzeit gemeldet"
              className="w-full bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-100 focus:border-amber-500 focus:outline-none"
            />
            <div className="text-[10px] text-zinc-500 mt-1">Mindestens 3 Zeichen.</div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onCancel}
                  data-testid="zr-reject-cancel"
                  className="text-sm px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100">
            Abbrechen
          </button>
          <button onClick={onConfirm}
                  disabled={!reasonValid}
                  data-testid="zr-reject-confirm"
                  className="text-sm px-4 py-2 rounded bg-red-600 hover:bg-red-700 disabled:bg-red-600/30 disabled:cursor-not-allowed text-white inline-flex items-center gap-1.5">
            <XCircle className="w-4 h-4" /> Ablehnen
          </button>
        </div>
      </div>
    </div>
  );
}
